package com.advaita.cloud.drive.ui.api

import androidx.compose.runtime.NoLiveLiterals
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.await
import kotlin.js.Promise

/**
 * Cross-tab liveness for upload sessions, built on the Web Locks API.
 *
 * Resume records live in IndexedDB, which every same-profile tab shares — so a
 * record alone can't distinguish "interrupted" from "actively uploading in
 * another tab". The uploading tab [hold]s an exclusive lock named after the
 * sessionId for the upload's lifetime; the browser releases it automatically
 * when that tab closes or crashes. Scans use [heldSessions] to skip live
 * sessions and [onReleased] to re-scan the moment the holder dies or finishes,
 * so a genuine interruption surfaces without a reload.
 *
 * Every entry point degrades to a no-op when `navigator.locks` is unavailable
 * (all evergreen browsers have it), which restores the pre-liveness behavior:
 * records are offered based on server state alone.
 */
@NoLiveLiterals
object UploadSessionLocks {

    private const val LOCK_PREFIX = "advaita-drive-upload:"

    /** SessionIds with a release-waiter already queued from this page. */
    private val watching = mutableSetOf<String>()

    private val locks: dynamic
        get() = js("(typeof navigator !== 'undefined' && navigator.locks) || null")

    /** Handle on a held session lock. [release] is idempotent. */
    class Held internal constructor(private val releaseFn: () -> Unit) {
        private var released = false
        fun release() {
            if (released) return
            released = true
            releaseFn()
        }
    }

    /**
     * Hold [sessionId]'s lock until [Held.release] (or the tab dies). Suspends
     * until the lock is granted — immediate for a fresh sessionId, since ids are
     * unique; any queued [onReleased] waiter ahead of us releases instantly.
     * Returns null when the Web Locks API is unavailable.
     */
    suspend fun hold(sessionId: String): Held? {
        val l = locks ?: return null
        val granted = CompletableDeferred<() -> Unit>()
        val opts = jsObject()
        opts.mode = "exclusive"
        (l.request(LOCK_PREFIX + sessionId, opts) { _: dynamic ->
            // The lock stays held until this promise resolves; hand the resolver
            // out through `granted` so the caller controls the release.
            Promise<Unit> { resolve, _ -> granted.complete { resolve(Unit) } }
        } as Promise<dynamic>).catch { }
        return Held(granted.await())
    }

    /** SessionIds whose lock is currently held by ANY tab (this one included). */
    suspend fun heldSessions(): Set<String> {
        val l = locks ?: return emptySet()
        return runCatching {
            val snapshot = (l.query() as Promise<dynamic>).await()
            val out = mutableSetOf<String>()
            val held = snapshot.held
            val len = (held?.length as? Int) ?: 0
            for (i in 0 until len) {
                val name = held[i].name as? String ?: continue
                if (name.startsWith(LOCK_PREFIX)) out.add(name.substring(LOCK_PREFIX.length))
            }
            out
        }.getOrElse { emptySet() }
    }

    /**
     * Invoke [callback] once when [sessionId]'s lock is released by whichever
     * tab holds it — normal completion, tab close, or crash. Implemented by
     * queueing an exclusive request behind the holder and releasing it the
     * instant it's granted. At most one waiter per sessionId per page.
     */
    fun onReleased(sessionId: String, callback: () -> Unit) {
        val l = locks ?: return
        if (!watching.add(sessionId)) return
        val opts = jsObject()
        opts.mode = "exclusive"
        (l.request(LOCK_PREFIX + sessionId, opts) { _: dynamic -> null } as Promise<dynamic>)
            .then<Unit> { _: dynamic ->
                watching.remove(sessionId)
                callback()
            }
            .catch { _: Throwable -> watching.remove(sessionId) }
    }

    private fun jsObject(): dynamic = js("({})")
}
